import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RedirectCommand, Resolve, Router, RouterStateSnapshot } from "@angular/router";
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from "rxjs";
import { AuthService } from "./auth.service";
import { CustomerActivationRequest, ErrorCode, InitiateSessionRequest, InitiateSessionResponse } from "../general.model";
import { TransactionService } from "./transaction.service";
import { InstantBankTransferService } from "../instant-bank-transfer.service";
import { PwaHandshakeService } from "../pwa-handshake.service";
import { MessageService } from "primeng/api";
import { HandshakeService } from "./handshake.service";
import { apiEndpoints } from "./general.common";

@Injectable({
    providedIn: 'root'
})
export class SessionService implements Resolve<RedirectCommand | void> {
    private isScriptLoaded = false;
    private readonly diagnosticsKey = 'h5-session-diagnostics';

    constructor(
        private router: Router,
        private authService: AuthService,
        private transactionService: TransactionService,
        private instantBankTransferService: InstantBankTransferService,
        private pwaHandshakeService: PwaHandshakeService
    ) {}

    resolve(): Observable<RedirectCommand | void> {
        console.log('[SessionResolver] Waiting for super app journey context');
        this.writeDiagnostics({
          stage: 'waiting-for-journey',
          at: new Date().toISOString()
        });
    
        return this.pwaHandshakeService.waitForJourney().pipe(
          tap(context => {
            const correlationId = context.correlationId || crypto.randomUUID();

            console.log('[SessionResolver] Journey received:', {
              source: context.source,
              correlationId,
              hasToken: Boolean(context.micrositeToken),
              hasMsisdn: Boolean(context.msisdn)
            });
    
            localStorage.setItem(
              'superapp-context',
              JSON.stringify(context)
            );
    
            localStorage.setItem(
              'superapp-token',
              context.micrositeToken
            );

            localStorage.setItem(
              'correlation-id',
              correlationId
            );
    
            this.authService.setUUID(correlationId);
            this.pwaHandshakeService.startHeartbeat();

            this.writeDiagnostics({
              stage: 'journey-received',
              at: new Date().toISOString(),
              source: context.source,
              hasMobileAccessToken: Boolean(context.micrositeToken),
              correlationId,
              initiateUrl: apiEndpoints.initiate()
            });
          }),
    
          switchMap(context => {
            const correlationId =
              context.correlationId ||
              localStorage.getItem('correlation-id') ||
              crypto.randomUUID();

            const request: InitiateSessionRequest = {
              correlationId,
              mobileAccessToken: context.micrositeToken
            };
    
            console.log('[SessionResolver] Initiating session:', {
              correlationId: request.correlationId,
              hasToken: Boolean(request.mobileAccessToken)
            });

            this.writeDiagnostics({
              stage: 'initiating-session',
              at: new Date().toISOString(),
              hasMobileAccessToken: Boolean(request.mobileAccessToken),
              correlationId: request.correlationId,
              initiateUrl: apiEndpoints.initiate()
            });
    
            return this.authService.initiate(request);
          }),
    
          tap(response => {
            console.log('[SessionResolver] Session initiated:', response);

            const sessionToken: string | null =
              response?.diasporaSessionToken ||
              response?.data?.diasporaSessionToken ||
              response?.token ||
              null;

            const hasToken = Boolean(sessionToken);
            console.log('[SessionResolver] diasporaSessionToken obtained:', hasToken, sessionToken ? `${sessionToken.slice(0, 10)}...` : 'NONE');

            this.writeDiagnostics({
              stage: 'session-initiated',
              at: new Date().toISOString(),
              hasDiasporaSessionToken: hasToken,
              tokenPreview: sessionToken ? `${sessionToken.slice(0, 12)}...` : null,
              tokenType: response?.tokenType,
              activationStatus: response?.activationStatus,
              scopes: response?.scopes,
              initiateUrl: apiEndpoints.initiate()
            });

            if (sessionToken) {
              localStorage.setItem('token', sessionToken);
              this.authService.setDiasporaSessionToken(sessionToken);
            } else {
              console.warn('[SessionResolver] Warning: Response did not include a diasporaSessionToken!', response);
              throw new Error('No diasporaSessionToken received from session initiation');
            }
          }),

          map(() => new RedirectCommand(this.router.parseUrl('/amount'))),

          catchError(error => {
            console.error(
              '[SessionResolver] Handshake or initiation failed:',
              error
            );

            const isCorsOrNetwork = error?.status === 0;
            const isLocal = typeof window !== 'undefined' &&
              (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

            let debugCause = error?.message || 'Unknown error';
            if (isCorsOrNetwork && isLocal) {
              debugCause = 'CORS error / Server rejected localhost origin. Use the local proxy (npm run start:h5) to avoid cross-origin rejection.';
            } else if (error?.status === 401) {
              debugCause = '401 Unauthorized: mobileAccessToken is invalid or expired on backend.';
            } else if (error?.status === 404) {
              debugCause = '404 Not Found: Check initiate URL path (' + apiEndpoints.initiate() + ').';
            } else if (!error?.status && error?.message?.includes('mobileAccessToken')) {
              debugCause = 'Missing mobileAccessToken in launch URL.';
            }

            this.writeDiagnostics({
              stage: 'session-error',
              at: new Date().toISOString(),
              status: error?.status ?? 0,
              statusText: error?.statusText || (isCorsOrNetwork ? 'CORS / Origin Rejected' : 'Error'),
              url: error?.url || apiEndpoints.initiate(),
              message: error?.message,
              debugCause,
              isCorsOrNetwork,
              isLocalhost: isLocal,
              backendCode: error?.error?.error?.code || error?.error?.code,
              backendMessage: error?.error?.error?.message || error?.error?.message,
              hasDiasporaSessionToken: false
            });

            const isServerError = error.status >= 500;
            const isClientError = error.status >= 400 && error.status < 500;

            const isKnownError = Object.values(ErrorCode).includes( error.code as ErrorCode);

            if(isKnownError){
              this.router.navigateByUrl('/feature-not-available');
              return throwError(() => error);
            }

            if (isServerError || isClientError || !error.status) {
              this.router.navigateByUrl('/service-unavailable');
            }
        
            return throwError(() => error);
          })
        );
    }

    private writeDiagnostics(value: Record<string, unknown>): void {
      localStorage.setItem(
        this.diagnosticsKey,
        JSON.stringify(value)
      );
    }
}
